// src/pages/register.js
import React, { useState, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";

import Form from "react-validation/build/form";
import Input from "react-validation/build/input";
import CheckButton from "react-validation/build/button";

import { UserInputDto } from "../../generated/src";
import SwaggerContext from "../../SwaggerContext";
import { parseApiError, logApiError } from "../../utils/error-handler";
import appLogo from "../../resources/app.jpg";
import {
  REGISTER_FORM,
  VALIDATION_MESSAGES,
  validatePassword,
  validateUsername,
  isValidEmail,
  getRegisterErrorMessage,
} from "behaviour-analyzer-shared";

// Validaciones para react-validation
const required = (value) =>
  !value && <div className="alert alert-danger" role="alert">{VALIDATION_MESSAGES.required}</div>;

const validEmail = (value) =>
  !isValidEmail(value) && <div className="alert alert-danger" role="alert">{VALIDATION_MESSAGES.invalidEmail}</div>;

const vusername = (value) => {
  const error = validateUsername(value);
  return error && <div className="alert alert-danger" role="alert">{error}</div>;
};

const RegisterForm = () => {
  const form = useRef();
  const checkBtn = useRef();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [successful, setSuccessful] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const navigate = useNavigate();
  const { userApi } = useContext(SwaggerContext);

  const vpasswordStrong = (value) => {
    const error = validatePassword(value);
    return error && <div className="alert alert-danger" role="alert">{error}</div>;
  };

  const vpasswordsMatch = (value, _props, components) => {
    const pwd = components.password?.[0]?.value ?? "";
    if (value !== pwd) {
      return <div className="alert alert-danger" role="alert">{VALIDATION_MESSAGES.passwordsNoMatch}</div>;
    }
  };

  const handleRegister = (e) => {
    e.preventDefault();
    setSuccessful(false);
    setSuccessMsg("");
    setErrorMsg("");

    form.current.validateAll();
    if (checkBtn.current.context._errors.length > 0) return;

    const registerRequestDto = new UserInputDto(
      firstName.trim(),
      email.trim(),
      username.trim(),
      lastName.trim(),
      password
    );

    userApi.register(registerRequestDto, (error, data, response) => {
      if (error) {
        logApiError("Register", error, response);
        const errorInfo = parseApiError(error, response);
        setErrorMsg(getRegisterErrorMessage(errorInfo.error) || errorInfo.message);
        setSuccessful(false);
        return;
      }

      setSuccessful(true);
      setSuccessMsg(REGISTER_FORM.messages.success);

      const preUser = data?.username ?? username;
      const prePass = data?.password ?? password;

      setTimeout(() => {
        navigate("/login", { state: { username: preUser, password: prePass } });
      }, 5000);
    });
  };

  return (
    <div style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100vh",
      backgroundColor: "#f5f5f5",
      padding: "20px",
    }}>
      <div style={{ width: "100%", maxWidth: "420px" }}>
        <img
          src={appLogo}
          alt="CEAEmociones"
          style={{
            width: "96px",
            height: "96px",
            borderRadius: "50%",
            display: "block",
            margin: "0 auto 20px",
            objectFit: "contain",
          }}
        />
        <h1 style={{
          fontSize: "32px",
          fontWeight: "bold",
          textAlign: "center",
          marginBottom: "10px",
        }}>
          {REGISTER_FORM.title}
        </h1>
        <p style={{
          fontSize: "16px",
          color: "#666",
          textAlign: "center",
          marginBottom: "30px",
        }}>
          {REGISTER_FORM.subtitle}
        </p>

        <Form onSubmit={handleRegister} ref={form}>
          {!successful && (
            <>
              <div className="form-group mb-3">
                <label htmlFor="firstName" style={{ fontSize: "14px", fontWeight: "600", color: "#333", marginBottom: "5px", display: "block" }}>{REGISTER_FORM.labels.name}</label>
                <Input
                  type="text"
                  className="form-control form-control-lg"
                  name="firstName"
                  placeholder={REGISTER_FORM.placeholders.name}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  validations={[required]}
                  style={{ borderRadius: "8px", border: "1px solid #ddd", padding: "15px", fontSize: "16px" }}
                />
              </div>

              <div className="form-group mb-3">
                <label htmlFor="lastName" style={{ fontSize: "14px", fontWeight: "600", color: "#333", marginBottom: "5px", display: "block" }}>{REGISTER_FORM.labels.surname}</label>
                <Input
                  type="text"
                  className="form-control form-control-lg"
                  name="lastName"
                  placeholder={REGISTER_FORM.placeholders.surname}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  validations={[required]}
                  style={{ borderRadius: "8px", border: "1px solid #ddd", padding: "15px", fontSize: "16px" }}
                />
              </div>

              <div className="form-group mb-3">
                <label htmlFor="email" style={{ fontSize: "14px", fontWeight: "600", color: "#333", marginBottom: "5px", display: "block" }}>{REGISTER_FORM.labels.email}</label>
                <Input
                  type="text"
                  className="form-control form-control-lg"
                  name="email"
                  placeholder={REGISTER_FORM.placeholders.email}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  validations={[required, validEmail]}
                  style={{ borderRadius: "8px", border: "1px solid #ddd", padding: "15px", fontSize: "16px" }}
                />
              </div>

              <div className="form-group mb-3">
                <label htmlFor="username" style={{ fontSize: "14px", fontWeight: "600", color: "#333", marginBottom: "5px", display: "block" }}>{REGISTER_FORM.labels.username}</label>
                <Input
                  type="text"
                  className="form-control form-control-lg"
                  name="username"
                  placeholder={REGISTER_FORM.placeholders.username}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  validations={[required, vusername]}
                  style={{ borderRadius: "8px", border: "1px solid #ddd", padding: "15px", fontSize: "16px" }}
                />
              </div>

              <div className="form-group mb-3">
                <label htmlFor="password" style={{ fontSize: "14px", fontWeight: "600", color: "#333", marginBottom: "5px", display: "block" }}>{REGISTER_FORM.labels.password}</label>
                <Input
                  type="password"
                  className="form-control form-control-lg"
                  name="password"
                  placeholder={REGISTER_FORM.placeholders.password}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  validations={[required, vpasswordStrong]}
                  style={{ borderRadius: "8px", border: "1px solid #ddd", padding: "15px", fontSize: "16px" }}
                />
              </div>

              <div className="form-group mb-3">
                <label htmlFor="confirmPassword" style={{ fontSize: "14px", fontWeight: "600", color: "#333", marginBottom: "5px", display: "block" }}>{REGISTER_FORM.labels.confirmPassword}</label>
                <Input
                  type="password"
                  className="form-control form-control-lg"
                  name="confirmPassword"
                  placeholder={REGISTER_FORM.placeholders.confirmPassword}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  validations={[required, vpasswordsMatch]}
                  style={{ borderRadius: "8px", border: "1px solid #ddd", padding: "15px", fontSize: "16px" }}
                />
              </div>

              <div className="form-group mt-3">
                <button
                  className="btn w-100"
                  style={{
                    backgroundColor: "#27ae60",
                    color: "#fff",
                    borderRadius: "8px",
                    padding: "15px",
                    fontSize: "18px",
                    fontWeight: "bold",
                    border: "none",
                  }}
                >
                  {REGISTER_FORM.buttons.submit}
                </button>
              </div>
            </>
          )}

          {successful && successMsg && (
            <div className="form-group">
              <div className="alert alert-success" role="alert">
                {successMsg}
              </div>
            </div>
          )}

          {!successful && errorMsg && (
            <div className="form-group mt-4">
              <div className="alert alert-danger" role="alert">
                {errorMsg}
              </div>
            </div>
          )}

          <CheckButton style={{ display: "none" }} ref={checkBtn} />
        </Form>

        <div className="text-center" style={{ marginTop: "20px" }}>
          <button
            type="button"
            className="btn btn-link"
            onClick={() => navigate("/login")}
            style={{ color: "#3498db", fontSize: "16px", textDecoration: "none" }}
          >
            {REGISTER_FORM.buttons.loginLink}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;
